import React, { useMemo } from 'react';
import type { BriefingSummary, BriefingOnboarding } from './EventBriefing';

/**
 * Event Journey Summary — a dashboard-style, at-a-glance summary of a single
 * event, modelled on the "event overview" dashboard layout but reframed around
 * the GUEST EXPERIENCE JOURNEY rather than hotel/room-block logistics.
 *
 * It assembles everything client-side from data the dashboard already has
 * (the event summary + onboarding checklist) — no extra round-trip. Three bands:
 *   1. Header — event name + date/time + venue.
 *   2. Event Overview — the timeline to the event with a live "days until" pill.
 *   3. Guest Experience Journey — a capacity donut + key metrics, and a
 *      five-stage journey strip (Plan → Invite → Prepare → Host → Follow-through)
 *      whose completion is derived from onboarding + live counts.
 *
 * The capacity numbers mirror the room-block panel we are replacing: where the
 * reference showed "room nights picked up / total in block / available", this
 * shows "guests registered / capacity / capacity remaining".
 */

export interface EventJourneySummaryProps {
  eventName: string;
  /** The owning organization (tenant). Shown prominently in the header band. */
  tenantName: string | null;
  isTicketed: boolean;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  venueName: string | null;
  maxCapacity: number | null;
  summary: BriefingSummary | null;
  onboarding: BriefingOnboarding | null;
}

const GREEN = '#1a6b3c';
const DARK = '#0f2d1f';
const GOLD = '#c9a84c';
const MUTED = '#5b7366';
const BORDER = '#e0e0e0';

/** Format a date-only ISO string as a readable local date (no UTC shift). */
function formatDate(iso: string | null): string {
  if (!iso) return 'Date TBD';
  const datePart = iso.includes('T') ? iso.split('T')[0] : iso;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return 'Date TBD';
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Format an event schedule time. Event times are stored as Eastern but
 * serialized as ISO (which looks like UTC) — parse the clock portion directly
 * so we never shift the displayed time through a timezone conversion.
 */
function formatTime(iso: string | null): string {
  if (!iso) return '';
  let hours: number, minutes: number;
  if (iso.includes('T')) {
    const timePart = iso.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (iso.includes(':')) {
    [hours, minutes] = iso.split(':').map(Number);
  } else {
    return '';
  }
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/** Whole-day difference between the event date and today (local). */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const datePart = iso.includes('T') ? iso.split('T')[0] : iso;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  const event = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((event.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

/** The urgency pill, mirroring the reference "60 days until cutoff" chip. */
function countdown(days: number | null): { text: string; tone: 'future' | 'soon' | 'today' | 'past' } {
  if (days === null) return { text: 'Date not set', tone: 'future' };
  if (days === 0) return { text: 'Happening today', tone: 'today' };
  if (days < 0) {
    const past = Math.abs(days);
    return { text: past === 1 ? 'Completed yesterday' : `Completed ${past} days ago`, tone: 'past' };
  }
  if (days === 1) return { text: '1 day until event', tone: 'soon' };
  if (days <= 14) return { text: `${days} days until event`, tone: 'soon' };
  return { text: `${days} days until event`, tone: 'future' };
}

function pillTone(tone: 'future' | 'soon' | 'today' | 'past'): React.CSSProperties {
  switch (tone) {
    case 'today': return { background: 'rgba(201,168,76,0.18)', color: '#92400e' };
    case 'soon': return { background: '#fdecea', color: '#b23c17' };
    case 'past': return { background: '#eef1ef', color: '#4b5563' };
    default: return { background: 'rgba(26,107,60,0.12)', color: '#166534' };
  }
}

function money(n: number): string {
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

type Stage = {
  key: string;
  label: string;
  caption: string;
  done: boolean;
};

/**
 * Derive the five guest-journey stages from onboarding + live counts.
 * Each stage reports whether it is "reached" and a one-line caption of the
 * real signal behind it.
 */
function buildStages(
  summary: BriefingSummary | null,
  onboarding: BriefingOnboarding | null,
  isTicketed: boolean,
  daysLeft: number | null,
): Stage[] {
  const g = summary?.guestCount ?? 0;
  const seated = summary && summary.seatCount > 0
    ? Math.round((summary.assignedSeats / summary.seatCount) * 100) : 0;
  const checkedIn = summary?.checkedIn ?? 0;
  const ob = (key: string) => onboarding?.items.find((i) => i.key === key)?.done ?? false;

  // Plan — foundational setup done (venue + modules + tiers if ticketed).
  const planDone = ob('venue') && ob('modules') && (!isTicketed || ob('tiers'));
  // Invite — any guests on the list / RSVPs captured.
  const inviteDone = g > 0 || ob('guests') || ob('orders');
  // Prepare — tickets sold (ticketed) or guest list built out (free), schedule shaping.
  const prepareDone = isTicketed ? (summary?.ticketsSold ?? 0) > 0 : g > 0;
  // Host — seats assigned / check-in underway (floorplan + seating).
  const hostDone = seated > 0 || checkedIn > 0 || ob('seating');
  // Follow-through — the event has happened (past) and people were checked in.
  const followDone = (daysLeft !== null && daysLeft < 0) && checkedIn > 0;

  return [
    { key: 'plan', label: 'Plan', caption: planDone ? 'Setup complete' : 'Configure venue & modules', done: planDone },
    { key: 'invite', label: 'Invite', caption: g > 0 ? `${g.toLocaleString()} on the list` : 'Build your guest list', done: inviteDone },
    {
      key: 'prepare',
      label: 'Prepare',
      caption: isTicketed
        ? ((summary?.ticketsSold ?? 0) > 0 ? `${summary!.ticketsSold.toLocaleString()} tickets sold` : 'Open ticket sales')
        : (g > 0 ? 'Guest list in progress' : 'Add guests & agenda'),
      done: prepareDone,
    },
    { key: 'host', label: 'Host', caption: checkedIn > 0 ? `${checkedIn.toLocaleString()} checked in` : (seated > 0 ? `${seated}% seated` : 'Assign seats & check-in'), done: hostDone },
    { key: 'follow', label: 'Follow-through', caption: followDone ? 'Event wrapped' : 'Recap after the event', done: followDone },
  ];
}

export function EventJourneySummary({
  eventName,
  tenantName,
  isTicketed,
  date,
  startTime,
  endTime,
  venueName,
  maxCapacity,
  summary,
  onboarding,
}: EventJourneySummaryProps) {
  const daysLeft = useMemo(() => daysUntil(date), [date]);
  const cd = useMemo(() => countdown(daysLeft), [daysLeft]);
  const stages = useMemo(
    () => buildStages(summary, onboarding, isTicketed, daysLeft),
    [summary, onboarding, isTicketed, daysLeft],
  );

  const guests = summary?.guestCount ?? 0;
  const capacity = maxCapacity && maxCapacity > 0 ? maxCapacity : 0;
  const remaining = capacity > 0 ? Math.max(capacity - guests, 0) : 0;
  // % of capacity filled drives the donut. If no capacity is set, fall back to
  // the onboarding completion so the ring still conveys progress.
  const fillPct = capacity > 0
    ? Math.min(Math.round((guests / capacity) * 100), 100)
    : (onboarding?.percent ?? 0);
  const seatedPct = summary && summary.seatCount > 0
    ? Math.round((summary.assignedSeats / summary.seatCount) * 100) : 0;

  // Donut built from a conic-gradient (green filled portion, light remainder).
  const donut: React.CSSProperties = {
    width: 132, height: 132, borderRadius: '50%',
    background: `conic-gradient(${GREEN} ${fillPct * 3.6}deg, #e8efe9 0deg)`,
    display: 'grid', placeItems: 'center', flexShrink: 0,
  };

  const schedule = [formatTime(startTime), formatTime(endTime)].filter(Boolean).join(' – ');

  const metrics: { label: string; value: string }[] = capacity > 0
    ? [
        { label: 'Guests registered', value: guests.toLocaleString() },
        { label: 'Capacity', value: capacity.toLocaleString() },
        { label: 'Capacity remaining', value: remaining.toLocaleString() },
        { label: 'Checked in', value: (summary?.checkedIn ?? 0).toLocaleString() },
        { label: 'Seats assigned', value: `${seatedPct}%` },
        { label: isTicketed ? 'Ticket revenue' : 'On the list', value: isTicketed ? money(summary?.ticketRevenue ?? 0) : guests.toLocaleString() },
      ]
    : [
        { label: 'Guests registered', value: guests.toLocaleString() },
        { label: 'Checked in', value: (summary?.checkedIn ?? 0).toLocaleString() },
        { label: 'Seats assigned', value: `${seatedPct}%` },
        { label: isTicketed ? 'Tickets sold' : 'RSVPs', value: (summary?.ticketsSold ?? guests).toLocaleString() },
        { label: isTicketed ? 'Ticket revenue' : 'Setup complete', value: isTicketed ? money(summary?.ticketRevenue ?? 0) : `${onboarding?.percent ?? 0}%` },
      ];

  return (
    <section style={styles.wrap} aria-label={`Journey summary for ${eventName}`}>
      {/* Header band — the owning organization is the prominent primary line,
          the event name + schedule sit beneath it. */}
      <div style={styles.headerBand}>
        <div style={styles.titleLine}>
          {tenantName && (
            <>
              <span aria-hidden style={styles.orgMark}>🏢</span>
              <span style={styles.orgName}>{tenantName}</span>
              <span aria-hidden style={styles.titleSep}>|</span>
            </>
          )}
          <span style={styles.headerName}>{eventName}</span>
        </div>
        <span style={styles.headerMeta}>
          {formatDate(date)}{schedule ? ` · ${schedule}` : ''}
        </span>
      </div>

      {/* Event Overview — timeline + countdown */}
      <div style={styles.block}>
        <h3 style={styles.blockTitle}>Event Overview</h3>
        <div style={styles.overviewRow}>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Date</span>
            <span style={styles.overviewValue}>{formatDate(date)}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Time</span>
            <span style={styles.overviewValue}>{schedule || 'TBD'}</span>
          </div>
          <div style={styles.overviewItem}>
            <span style={styles.overviewLabel}>Venue</span>
            <span style={styles.overviewValue}>{venueName || 'TBD'}</span>
          </div>
          <span style={{ ...styles.countdownPill, ...pillTone(cd.tone) }}>{cd.text}</span>
        </div>
      </div>

      {/* Guest Experience Journey — donut + metrics */}
      <div style={styles.block}>
        <h3 style={styles.blockTitle}>Guest Experience Journey</h3>
        <div style={styles.journeyBody}>
          <div style={styles.donutWrap}>
            <div style={donut} role="img" aria-label={`${fillPct}% ${capacity > 0 ? 'of capacity filled' : 'set up'}`}>
              <div style={styles.donutHole}>
                <span style={styles.donutPct}>{fillPct}%</span>
                <span style={styles.donutCaption}>{capacity > 0 ? 'of capacity' : 'set up'}</span>
              </div>
            </div>
          </div>
          <div style={styles.metricGrid}>
            {metrics.map((m) => (
              <div key={m.label} style={styles.metric}>
                <span style={styles.metricValue}>{m.value}</span>
                <span style={styles.metricLabel}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Five-stage journey strip */}
        <div style={styles.stageStrip} aria-label="Guest journey stages">
          {stages.map((s, i) => (
            <div key={s.key} style={styles.stage}>
              <div style={styles.stageTop}>
                <span style={{ ...styles.stageDot, ...(s.done ? styles.stageDotDone : {}) }}>
                  {s.done ? '✓' : i + 1}
                </span>
                {i < stages.length - 1 && (
                  <span style={{ ...styles.stageConnector, ...(s.done ? styles.stageConnectorDone : {}) }} aria-hidden />
                )}
              </div>
              <span style={styles.stageLabel}>{s.label}</span>
              <span style={styles.stageCaption}>{s.caption}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    border: `1px solid ${BORDER}`, borderRadius: 16, background: '#ffffff',
    overflow: 'hidden', marginBottom: 20,
    boxShadow: '0 10px 30px -22px rgba(15,45,31,0.5)',
  },

  headerBand: {
    padding: '18px 22px', background: 'linear-gradient(135deg, #0f2d1f 0%, #1a6b3c 100%)',
    color: '#eafff2',
  },
  titleLine: { display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' },
  orgMark: { fontSize: '1.2rem', lineHeight: 1 },
  orgName: {
    fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.1,
    fontFamily: "Georgia, 'Playfair Display', serif", color: '#ffffff',
  },
  titleSep: { fontSize: '1.3rem', fontWeight: 300, color: 'rgba(214,243,225,0.5)', lineHeight: 1 },
  headerName: {
    fontSize: '1.35rem', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.1,
    fontFamily: "Georgia, 'Playfair Display', serif", color: '#d6f3e1',
  },
  headerMeta: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#9fd8b6', marginTop: 7, fontVariantNumeric: 'tabular-nums' },

  block: { padding: '18px 20px', borderTop: `1px solid ${BORDER}` },
  blockTitle: { margin: '0 0 14px', fontSize: '1rem', fontWeight: 800, color: DARK },

  overviewRow: { display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' },
  overviewItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  overviewLabel: { fontSize: '0.68rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' },
  overviewValue: { fontSize: '0.95rem', fontWeight: 700, color: DARK },
  countdownPill: { marginLeft: 'auto', fontSize: '0.78rem', fontWeight: 800, borderRadius: 999, padding: '6px 14px', whiteSpace: 'nowrap' },

  journeyBody: { display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' },
  donutWrap: { display: 'grid', placeItems: 'center' },
  donutHole: {
    width: 96, height: 96, borderRadius: '50%', background: '#fff',
    display: 'grid', placeItems: 'center', textAlign: 'center',
  },
  donutPct: { fontSize: '1.5rem', fontWeight: 800, color: DARK, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  donutCaption: { fontSize: '0.66rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 3 },

  metricGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))',
    gap: '16px 24px', flex: 1, minWidth: 280,
  },
  metric: { display: 'flex', flexDirection: 'column', gap: 2 },
  metricValue: { fontSize: '1.4rem', fontWeight: 800, color: DARK, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  metricLabel: { fontSize: '0.72rem', fontWeight: 600, color: MUTED, marginTop: 3 },

  stageStrip: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10,
    marginTop: 22, paddingTop: 18, borderTop: `1px solid ${BORDER}`,
  },
  stage: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  stageTop: { position: 'relative', display: 'flex', alignItems: 'center', height: 28 },
  stageDot: {
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
    display: 'grid', placeItems: 'center', fontSize: '0.78rem', fontWeight: 800,
    background: '#eef1ef', color: MUTED, border: `2px solid ${BORDER}`,
  },
  stageDotDone: { background: GREEN, color: '#fff', border: `2px solid ${GREEN}` },
  stageConnector: { flex: 1, height: 2, background: BORDER, marginLeft: 4 },
  stageConnectorDone: { background: GREEN },
  stageLabel: { fontSize: '0.82rem', fontWeight: 800, color: DARK },
  stageCaption: { fontSize: '0.72rem', color: MUTED, lineHeight: 1.35 },
};
